<template>
  <div class="dm">
    <button
      ref="trigger"
      type="button"
      class="dm-trigger"
      @click="toggle"
    >
      <feather-icon
        icon="MoreHorizontalIcon"
        size="15"
      />
    </button>

    <!--
      @click.stop on the panel prevents clicks inside it from reaching the document
      listener (which would otherwise close the panel via onOutsideClick).
      position:fixed coordinates are calculated at open time via getBoundingClientRect.
    -->
    <div
      v-if="isOpen"
      class="dm-panel"
      :style="panelStyle"
      @click.stop
    >
      <p class="dm-title">
        Download data
      </p>

      <div class="dm-formats">
        <button
          v-for="fmt in FORMATS"
          :key="fmt"
          type="button"
          class="dm-fmt"
          :class="{ 'dm-fmt--on': format === fmt }"
          @click="format = fmt"
        >.{{ fmt }}</button>
      </div>

      <label class="dm-check-row">
        <input
          v-model="keepFormatted"
          type="checkbox"
          class="dm-check"
        >
        <span>
          <strong>Keep the data formatted</strong><br>
          <span class="dm-hint">E.g. March 29, 2026 or EUR 533.34, like in Metabase</span>
        </span>
      </label>

      <button
        type="button"
        class="dm-download"
        @click="download"
      >
        Download
      </button>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'

const FORMATS = ['csv', 'xlsx', 'json']

export default {
  name: 'DownloadMenu',
  props: {
    filename: { type: String, default: 'data' },
    rows: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
  },
  data() {
    return {
      FORMATS,
      isOpen: false,
      format: 'csv',
      keepFormatted: true,
      panelStyle: {},
    }
  },
  mounted() {
    document.addEventListener('click', this.onOutsideClick)
    window.addEventListener('scroll', this.close, true)
    window.addEventListener('resize', this.close)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onOutsideClick)
    window.removeEventListener('scroll', this.close, true)
    window.removeEventListener('resize', this.close)
  },
  methods: {
    toggle() {
      if (this.isOpen) {
        this.isOpen = false
        return
      }
      const rect = this.$refs.trigger.getBoundingClientRect()
      this.panelStyle = {
        position: 'fixed',
        top: `${rect.bottom + 6}px`,
        right: `${window.innerWidth - rect.right}px`,
        zIndex: 9999,
      }
      this.isOpen = true
    },

    close() {
      this.isOpen = false
    },

    // Only close when the click genuinely originated outside this component's root.
    // The trigger button and panel are both children of this.$el so contains()
    // returns true for them and we leave the state untouched.
    onOutsideClick(e) {
      if (this.$el && this.$el.contains(e.target)) return
      this.isOpen = false
    },

    buildExportRows() {
      return this.rows.map(row => {
        const out = {}
        this.columns.forEach(col => {
          const raw = row[col.key]
          out[col.label] = this.keepFormatted && col.format ? col.format(raw, row) : raw
        })
        return out
      })
    },

    download() {
      const data = this.buildExportRows()
      const name = this.filename.replace(/\s+/g, '_')

      if (this.format === 'json') {
        this.saveFile(JSON.stringify(data, null, 2), `${name}.json`, 'application/json')
        return
      }

      const ws = XLSX.utils.json_to_sheet(data)
      if (this.format === 'xlsx') {
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
        XLSX.writeFile(wb, `${name}.xlsx`)
        this.isOpen = false
        return
      }

      // csv
      const csv = XLSX.utils.sheet_to_csv(ws)
      this.saveFile(csv, `${name}.csv`, 'text/csv')
    },

    saveFile(content, filename, type) {
      const blob = new Blob([content], { type })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      this.isOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
.dm {
  position: relative;
  display: inline-flex;
}

.dm-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #94a3b8;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f1f5f9;
    color: #374151;
  }
}

/* Panel uses position:fixed set inline via :style — these rules only handle appearance */
.dm-panel {
  width: 260px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.13);
  padding: 14px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dm-title {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1e293b;
}

.dm-formats {
  display: flex;
  gap: 6px;
}

.dm-fmt {
  flex: 1;
  padding: 6px 4px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  font-size: 0.78rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s, background 0.15s, color 0.15s;

  &:hover {
    border-color: #93c5fd;
    background: #eff6ff;
    color: #1e40af;
  }

  &--on {
    border-color: #1e40af;
    background: #eff6ff;
    color: #1e40af;
  }
}

.dm-check-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.78rem;
  color: #374151;
  cursor: pointer;
  margin: 0;
  user-select: none;
  line-height: 1.4;
}

.dm-check {
  margin-top: 2px;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  accent-color: #1e40af;
  cursor: pointer;
}

.dm-hint {
  font-size: 0.72rem;
  color: #94a3b8;
}

.dm-download {
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 7px;
  background: #1e40af;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #1d4ed8;
  }
}
</style>
